/**
 * Testimonials Section
 * Использует переиспользуемые компоненты
 * Примечание: использует useState для карусели в редакторе
 */
import React, { useState, useRef } from 'react';
import { Text, Icon, Image, Container } from '../components';

// Хелпер для получения data объекта с кавычками
const getQuotedTextData = (value) => {
  if (typeof value === 'object' && value !== null) {
    return { ...value, text: `"${value.text || ''}"` };
  }
  return { text: `"${value || ''}"` };
};

export default function TestimonialsSection({ data, styles }) {
  const testimonials = data?.testimonials || [];
  const sectionStyle = data?.style || 'cards';
  const columns = data?.columns || 2;
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);

  const renderRating = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Icon
            key={star}
            name="Star"
            size="sm"
            className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const scrollToSlide = (index) => {
    if (sliderRef.current) {
      const slideWidth = sliderRef.current.children[0]?.offsetWidth || 0;
      sliderRef.current.scrollTo({
        left: index * slideWidth,
        behavior: 'smooth'
      });
      setCurrentSlide(index);
    }
  };

  const handleScroll = () => {
    if (sliderRef.current && sliderRef.current.children[0]) {
      const slideWidth = sliderRef.current.children[0].offsetWidth;
      const scrollLeft = sliderRef.current.scrollLeft;
      const newSlide = Math.round(scrollLeft / slideWidth);
      if (newSlide !== currentSlide && newSlide >= 0 && newSlide < testimonials.length) {
        setCurrentSlide(newSlide);
      }
    }
  };

  const nextSlide = () => {
    if (currentSlide < testimonials.length - 1) {
      scrollToSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      scrollToSlide(currentSlide - 1);
    }
  };

  const TestimonialCard = ({ item, index, isCarousel = false }) => (
    <div
      className={`bg-card border border-border rounded-2xl p-6 md:p-8 ${
        isCarousel ? '' : 'hover:shadow-lg transition-shadow'
      }`}
    >
      <div className="flex items-start gap-4 mb-4">
        {item.avatar && (
          <Image
            src={item.avatar}
            alt={item.name}
            className="w-12 h-12 md:w-14 md:h-14 flex-shrink-0"
            rounded="full"
            fit="cover"
          />
        )}
        <div className="flex-1 min-w-0">
          <Text
            as="h4"
            defaults={{ size: 'lg', weight: 'semibold', color: 'foreground' }}
            className="truncate"
          >
            {item.name}
          </Text>
          {(item.position || item.company) && (
            <Text
              as="p"
              defaults={{ size: 'sm', weight: 'normal', color: 'text-secondary' }}
              className="truncate"
            >
              {item.position}{item.position && item.company && ', '}{item.company}
            </Text>
          )}
        </div>
      </div>

      {item.rating && renderRating(item.rating)}

      <Text
        as="p"
        data={getQuotedTextData(item.text)}
        defaults={{ size: 'base', weight: 'normal', color: 'text-secondary' }}
        className="mt-4 leading-relaxed"
      />
    </div>
  );

  const renderCardsStyle = () => (
    <div
      className="grid grid-cols-1 gap-6"
      style={{
        gridTemplateColumns: `repeat(1, 1fr)`
      }}
    >
      <style>{`
        @media (min-width: 768px) {
          .testimonials-grid {
            grid-template-columns: repeat(${Math.min(columns, 2)}, 1fr) !important;
          }
        }
        @media (min-width: 1024px) {
          .testimonials-grid {
            grid-template-columns: repeat(${columns}, 1fr) !important;
          }
        }
      `}</style>
      <div className="testimonials-grid grid grid-cols-1 gap-6">
        {testimonials.map((item, index) => (
          <TestimonialCard key={item.id || index} item={item} index={index} />
        ))}
      </div>
    </div>
  );

  const renderCarouselStyle = () => (
    <div className="relative">
      {/* Slider container */}
      <div
        ref={sliderRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory gap-4 md:gap-6 pb-4"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {testimonials.map((item, index) => (
          <div
            key={item.id || index}
            className="flex-shrink-0 w-[85%] sm:w-[70%] md:w-[45%] lg:w-[35%] snap-start"
          >
            <TestimonialCard item={item} index={index} isCarousel />
          </div>
        ))}
      </div>

      {/* Navigation arrows - desktop */}
      {testimonials.length > 2 && (
        <>
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white hover:bg-gray-50 rounded-full items-center justify-center shadow-lg border border-border transition-all disabled:opacity-30 disabled:cursor-not-allowed z-10"
          >
            <Icon name="ChevronLeft" size={24} />
          </button>
          <button
            onClick={nextSlide}
            disabled={currentSlide >= testimonials.length - 1}
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white hover:bg-gray-50 rounded-full items-center justify-center shadow-lg border border-border transition-all disabled:opacity-30 disabled:cursor-not-allowed z-10"
          >
            <Icon name="ChevronRight" size={24} />
          </button>
        </>
      )}

      {/* Dots indicator */}
      {testimonials.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide
                  ? 'bg-primary w-6'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );

  const renderQuotesStyle = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
      {testimonials.map((item, index) => (
        <div
          key={item.id || index}
          className="relative bg-card rounded-2xl p-6 md:p-8"
        >
          <Icon
            name="Quote"
            size={28}
            className="absolute top-6 right-6 text-primary/15"
          />

          <Text
            as="p"
            data={getQuotedTextData(item.text)}
            defaults={{ size: 'lg', weight: 'normal', color: 'foreground' }}
            className="leading-relaxed mb-6 pr-8"
          />

          <div className="flex items-center gap-4">
            {item.avatar && (
              <Image
                src={item.avatar}
                alt={item.name}
                className="w-12 h-12 rounded-full object-cover"
              />
            )}
            <div>
              <h4 className="font-semibold">{item.name}</h4>
              {(item.position || item.company) && (
                <p className="text-sm text-text-secondary">
                  {item.position}{item.position && item.company && ', '}{item.company}
                </p>
              )}
              {item.rating && <div className="mt-1">{renderRating(item.rating)}</div>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderMinimalStyle = () => (
    <div className="max-w-4xl mx-auto space-y-8 md:space-y-12">
      {testimonials.map((item, index) => (
        <div
          key={item.id || index}
          className="text-center"
        >
          <Text
            as="p"
            data={getQuotedTextData(item.text)}
            defaults={{ size: '2xl', weight: 'normal', color: 'foreground' }}
            className="leading-relaxed mb-6"
          />

          <div className="flex flex-col items-center gap-3">
            {item.avatar && (
              <Image
                src={item.avatar}
                alt={item.name}
                className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover"
              />
            )}
            <div>
              <h4 className="font-semibold text-lg">{item.name}</h4>
              {(item.position || item.company) && (
                <p className="text-sm text-text-secondary">
                  {item.position}{item.position && item.company && ', '}{item.company}
                </p>
              )}
            </div>
            {item.rating && renderRating(item.rating)}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <section
      style={{
        paddingTop: styles?.paddingTop || 64,
        paddingBottom: styles?.paddingBottom || 64,
        backgroundColor: styles?.backgroundColor || '#FFFFFF'
      }}
    >
      <Container>
        {/* Заголовок */}
        <Text
          as="h2"
          data={typeof data?.title === 'object' ? data.title : { text: data?.title }}
          defaults={{ size: '3xl', weight: 'bold', color: 'foreground' }}
          align="center"
          className={data?.subtitle ? 'mb-2 md:mb-3' : 'mb-8 md:mb-12'}
        />

        {/* Подзаголовок */}
        <Text
          as="p"
          data={typeof data?.subtitle === 'object' ? data.subtitle : { text: data?.subtitle }}
          defaults={{ size: 'lg', weight: 'normal', color: 'text-secondary' }}
          align="center"
          className="mb-8 md:mb-12"
        />

        {testimonials.length > 0 ? (
          <>
            {sectionStyle === 'cards' && renderCardsStyle()}
            {sectionStyle === 'carousel' && renderCarouselStyle()}
            {sectionStyle === 'quotes' && renderQuotesStyle()}
            {sectionStyle === 'minimal' && renderMinimalStyle()}
          </>
        ) : (
          <div className="text-center py-12 text-text-secondary">
            <Icon name="MessageCircle" size="3xl" className="mx-auto mb-4 opacity-50" />
            <p>Добавьте отзывы в настройках</p>
          </div>
        )}
      </Container>
    </section>
  );
}
